<template>
  <div :class="{ 'wc-page-wrapper': !scanMode, 'wc-scan-wrapper container': scanMode }">
    <div class="row" :class="{ 'm-0 p-0': scanMode }">
      <template v-if="item && (!scanMode || isScanModePDP)">
        <WCMobileSubpageHeader
          v-if="isMobile"
          :title="$route.meta.title ? $route.meta.title : $route.name"
        />
        <div v-if="isMobile" />
        <!-- Show full width image if there is only one product image -->
        <div v-if="item.images && item.images.length === 1" class="product-img w-100 mx-3 mb-3">
          <WCImageWithShadow
            :imageSrc="itemImageSrc(item.images[0])"
            :alt="productImgAltText(item.name, item.brand)"
            class="wc-img--full-size"
            :defaultTitle="noProductImageAltText(item.name)"
          />
          <div class="wc-promo-ribbon-wrap">
            <WCPromoRibbon :product="item" :ribbonType="promoRibbonType.SALE_AMOUNT_RIBBON" />
            <WCPromoRibbon :product="item" :ribbonType="promoRibbonType.DISCOUNT_NAME_RIBBON" />
          </div>
        </div>
        <!-- Else show carousel fixed image if there is more than one product image -->
        <WCCarousel
          class="wc-carousel--negate-padding py-2 d-flex mx-2 w-100"
          v-else-if="item.images && item.images.length > 1"
          :isSlider="true"
        >
          <Splide
            :options="{
              type: 'slide',
              pagination: true,
              arrows: false,
              gap: '1rem',
            }"
            class="container-fluid pb-4"
            role="list"
          >
            <SplideSlide v-for="image in item.images" :key="image.id">
              <WCImageWithShadow
                :imageSrc="itemImageSrc(image)"
                :alt="productImgAltText(item.name, item.brand)"
                class="wc-img--full-size"
                :defaultTitle="noProductImageAltText(item.name)"
              />
              <div class="wc-promo-ribbon-wrap">
                <WCPromoRibbon :product="item" :ribbonType="promoRibbonType.SALE_AMOUNT_RIBBON" />
                <WCPromoRibbon :product="item" :ribbonType="promoRibbonType.DISCOUNT_NAME_RIBBON" />
              </div>
            </SplideSlide>
          </Splide>
        </WCCarousel>

        <WCProductNameMobile class="m-3" :item="item" />
        <WCProductPricing
          :item="item"
          :embeddedPrice="priceEmbeddedLineItem ? priceEmbeddedLineItem.embeddedPrice : null"
          v-if="!shouldHideItemPrices"
          class="text-right col-10"
        />
      </template>

      <!-- START : Product Details View for Scan and Go -->
      <div v-else-if="item && scanMode" class="row align-items-center w-100 m-0 p-0">
        <!-- Start: Item name & Brand -->
        <div class="container-fluid">
          <div class="row justify-content-center">
            <div class="d-flex flex-column">
              <router-link :to="{ path: `/i/${item.id}`, query: { scan: scanMode } }">
                <div class="text-center text-danger text-small" v-if="itemNotInList">
                  {{ $t('notOnYourList') }}
                </div>
                <h2 class="font-weight-bold text-center">
                  {{ item.name
                  }}<span class="alternate-name" v-if="item.alternateName">
                    - {{ item.alternateName }}</span
                  >
                </h2>
              </router-link>
              <h3 class="text-center font-weight-bold">{{ item.brand }}</h3>
            </div>
          </div>
        </div>
        <!-- End: Item name & Brand -->

        <!-- START: Picture, Pricing, and Ribbons -->
        <div class="row mx-auto w-100 p-0 m-0">
          <button class="unstyled-btn w-50 px-1" @click="$emit('handleProductView')">
            <WCProductDetailsImage :item="item" :scanMode="true" />
          </button>

          <div class="w-50 px-1 fit-content">
            <WCProductPricing
              class="price-text-size"
              :item="item"
              :embeddedPrice="priceEmbeddedLineItem ? priceEmbeddedLineItem.embeddedPrice : null"
              :isScanMode="true"
            ></WCProductPricing>

            <div class="wc-promo-ribbon-wrap">
              <WCRestrictedItemRibbon
                class="mb-1 wc-red-ribbon"
                v-if="
                  scanMode &&
                    (item.hasTimeRestrictions ||
                      item.cashierApprovalRequired ||
                      item.hasAgeRestriction ||
                      (item.hasRestriction && !item.excludeFromCombos) ||
                      (!priceEmbeddedLineItem && item.weightProfile))
                "
                :item="item"
              />
            </div>
          </div>
        </div>
        <!-- END: Picture, Pricing, and Ribbons -->

        <!-- START: Add to card & list buttons -->
        <div class="row w-100 px-2 m-0">
          <div
            class="my-3 mx-auto px-1 col"
            v-if="isScanAddToListButtonEnabled && !priceEmbeddedLineItem"
          >
            <WCAddToListButtonMobile
              class="w-100"
              :item="item"
              v-if="!item.hasModifiers && !item.hasLinkedItems"
              :showText="true"
            />
            <div
              class="mx-auto w-100 px-1 text-center"
              v-else-if="
                (item.hasModifiers ||
                  item.hasLinkedItems ||
                  (item.hasModifiers && !$configuration.cartEnabled) ||
                  (item.hasLinkedItems && !$configuration.cartEnabled)) &&
                  !priceEmbeddedLineItem
              "
            >
              <button
                class="btn btn-outline-primary w-100"
                @click="customizeItemToAddToList(true)"
                ref="customizeBtnRef"
                :aria-label="$t('customizeAndAddToList')"
              >
                <span class="mr-3">{{ $t('customizeAndAddToList') }}</span>
                <font-awesome-icon icon="list-check" />
              </button>
            </div>
          </div>

          <WCBasicAddToCartButton
            class="my-3 mx-auto px-1 col"
            :showMobileCustomize="true"
            :item="item"
            :priceEmbeddedLineItem="priceEmbeddedLineItem"
            :orderType="!isScanAndGoMode ? activeOrderType : null"
            v-if="isScanAddToCartButtonEnabled"
          />
        </div>
        <!-- END: Add to card & list buttons -->
      </div>
      <!-- END : Product Details View for Scan and Go -->

      <div class="col-12" v-if="!scanMode">
        <div
          class="row mx-auto"
          :class="{
            'justify-content-between': !isGuest,
            'justify-content-center': isGuest,
          }"
          v-if="!shouldHideItemPrices"
        >
          <WCBasicAddToCartButton
            :showMobileCustomize="true"
            :item="item"
            class="btn-block col-sm-6 col-9 p-0 mr-auto"
            :showAddToCartOption="showAddToCartButton"
            :orderType="!isScanAndGoMode ? activeOrderType : null"
          />
          <!-- START : Add to list button -->
          <template v-if="!isItemListOnly(item)">
            <WCAddToListButtonMobile
              class="ml-auto"
              :item="item"
              :isGuest="isGuest"
              v-if="
                (!item.hasModifiers && !item.hasLinkedItems) ||
                  (!item.hasModifiers && !item.hasLinkedItems && !$configuration.cartEnabled)
              "
            />
            <div
              v-else-if="
                item.hasModifiers ||
                  item.hasLinkedItems ||
                  (item.hasModifiers && !$configuration.cartEnabled) ||
                  (item.hasLinkedItems && !$configuration.cartEnabled)
              "
            >
              <button
                class="btn btn-outline-primary ml-2"
                @click="customizeItemToAddToList(true)"
                ref="customizeBtnRef"
                :aria-label="$t('customizeAndAddToList')"
              >
                <font-awesome-icon icon="list-check" />
              </button>
            </div>
          </template>
        </div>
        <div v-else>
          <router-link class="btn btn-primary btn-block wc-atc-btn wc-atc-btn--basic" to="/login">
            {{ $t('signInToShop') }}
          </router-link>
        </div>

        <WCProductDetailsMobile v-if="!scanMode" class="mt-3 mb-3" :item="item" />
        <WCAlternateItems v-if="!scanMode" :item="item" :showAddToCartButton="!isScanAndGoMode" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import WCAddToListButtonMobile from '@/modules/lists/components/WCAddToListButtonMobile/WCAddToListButtonMobile.vue';
import WCProductNameMobile from '@/components/WCProductDetailsMobile/WCProductNameMobile.vue';
import WCProductDetailsMobile from '@/components/WCProductDetailsMobile/WCProductDetailsMobile.vue';
import WCCarousel from '@/components/WCCarousel/WCCarousel.vue';
import WCProductPricing from '@/components/WCProductPricing/WCProductPricing.vue';
import WCImageWithShadow from '@/components/WCImageWithShadow/WCImageWithShadow.vue';
import WCProductDetailsImage from '@/modules/lists/components/WCProductDetailsImage/WCProductDetailsImage.vue';
import ProductImageInfoMixin from '@/mixins/ProductImageInfoMixin';
import CartControlsMixin from '@/modules/cart/mixins/CartControls/CartControls';
import WCBasicAddToCartButton from '@/modules/cart/components/WCBasicAddToCartButton/WCBasicAddToCartButton.vue';
import WCAlternateItems from '@/components/WCAlternateItems/WCAlternateItems.vue';
import { PROMO_RIBBON_TYPE } from '@/constants/PromoRibbonConstants';
import WCPromoRibbon from '@/components/WCPromoRibbon/WCPromoRibbon.vue';
import OrderTypesMixin from '@/modules/orderTypes/mixins/OrderTypesMixin';
import HidePricingMixin from '@/mixins/HidePricingMixin';
import { defineComponent } from 'vue';
import WCMobileSubpageHeader from '@/components/WCMobileSubpageHeader/WCMobileSubpageHeader.vue';
import { PlatformMixin } from '@/modules/platform';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import ProductDetailMixin from './mixins/ProductDetailMixin';
import WCRestrictedItemRibbon from '../ScanProduct/components/WCRestrictedItemRibbon/WCRestrictedItemRibbon.vue';

export default defineComponent({
  props: {
    item: {
      type: Object,
      required: true,
    },
    itemNotInList: {
      type: Boolean,
      default: false,
    },
    scanMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    priceEmbeddedLineItem: {
      type: Object,
      required: false,
    },
  },
  emits: ['handleProductView'],
  components: {
    WCProductNameMobile,
    WCAddToListButtonMobile,
    WCProductDetailsMobile,
    WCCarousel,
    WCImageWithShadow,
    WCProductDetailsImage,
    WCProductPricing,
    WCAlternateItems,
    WCBasicAddToCartButton,
    WCPromoRibbon,
    WCRestrictedItemRibbon,
    WCMobileSubpageHeader,
    Splide,
    SplideSlide,
  },
  mixins: [
    CartControlsMixin,
    ProductImageInfoMixin,
    ProductDetailMixin,
    OrderTypesMixin,
    HidePricingMixin,
    PlatformMixin,
  ],
  computed: {
    ...mapGetters({
      isGuest: 'user/isGuest',
      isScanAndGoMode: 'user/isCustomerModeScan',
    }),
    /**
     * Method to get the ribbon type key
     */
    promoRibbonType() {
      return PROMO_RIBBON_TYPE;
    },
    isScanModePDP() {
      return this.$route.query.scan === 'true';
    },
    showAddToCartButton() {
      return !this.isScanAndGoMode || (this.isScanAndGoMode && this.isScanModePDP);
    },
    isScanAddToListButtonEnabled() {
      return this.$configuration.listEnabled;
    },
    isScanAddToCartButtonEnabled() {
      return this.$configuration.cartEnabled;
    },
  },
  methods: {
    itemImageSrc(image) {
      return image ? this.getProductImage(image.id) : this.getDefaultProductImg();
    },
  },
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-page-wrapper {
  min-height: rem(500px);
}

.wc-scan-wrapper {
  margin-top: 1.25rem;
  max-height: 80vh;
  overflow-y: scroll;
}

.product-img {
  position: relative;
  .wc-promo-ribbon-wrap {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}

.price-text-size {
  font-size: $font-size-xl !important;
}

.wc-red-ribbon {
  background-color: $danger !important;
}
</style>
